import { SubgraphType } from 'config/subgraph'
import { useConnection } from 'hooks/useConnection'
import { useSubgraphConfig } from 'hooks/useSubgraphConfig'
import { ChartDayData } from 'types/analytics'
import { AnalyticsChartSeries, buildAnalyticsChartSeries } from 'utils/analytics/analyticsChartSeries'

import type {
  GetV2GlobalChartQuery,
  GetV2PairChartQuery,
  GetV2TokenChartQuery,
} from '../../queries/analytics/v3/generated'
import {
  useGetV2GlobalChartQuery,
  useGetV2PairChartQuery,
  useGetV2TokenChartQuery,
} from '../../queries/analytics/v3/generated'

export type V2ChartQueryKeyOptions = {
  pairAddress?: string
  tokenAddress?: string
  skip?: number
  block?: number
  startTime?: number
}

export const v2ChartQueryKey = (chainId: number, options?: V2ChartQueryKeyOptions) =>
  [
    'analytics',
    'v2ChartData',
    chainId,
    options?.pairAddress ?? null,
    options?.tokenAddress ?? null,
    options?.skip ?? 0,
    options?.block ?? null,
    options?.startTime ?? null,
  ] as const

const mapV2DayData = (
  dayData: Array<{ date: number; dailyVolumeUSD: string | null | undefined; tvlUSD: string | null | undefined }>,
  startTime?: number
): AnalyticsChartSeries => {
  const mapped: ChartDayData[] = dayData
    .map((item) => {
      const volumeUSD = Number.parseFloat(item.dailyVolumeUSD ?? '0')
      const tvlUSD = Number.parseFloat(item.tvlUSD ?? '0')

      return {
        date: item.date,
        volumeUSD,
        tvlUSD,
        feesUSD: volumeUSD * 0.003,
      }
    })
    .filter((item) => (startTime ? item.date >= startTime : true))
    .sort((a, b) => b.date - a.date)

  return buildAnalyticsChartSeries(mapped)
}

type UseV2ChartDataOptions = {
  enabled?: boolean
  pairAddress?: string
  tokenAddress?: string
  startTime?: number
}

export const useV2ChartData = (options?: UseV2ChartDataOptions) => {
  const { chainId } = useConnection()
  const subgraphConfig = useSubgraphConfig(SubgraphType.V3)

  const endpoint = subgraphConfig.endpoint

  const baseEnabled = (options?.enabled ?? true) && Boolean(subgraphConfig)
  const pairAddress = options?.pairAddress
  const tokenAddress = options?.tokenAddress
  const startTime = options?.startTime

  const pairEnabled = baseEnabled && Boolean(pairAddress)
  const tokenEnabled = baseEnabled && !pairAddress && Boolean(tokenAddress)
  const globalEnabled = baseEnabled && !pairAddress && !tokenAddress

  const pairQuery = useGetV2PairChartQuery<AnalyticsChartSeries>(
    {
      endpoint,
    },
    {
      pairAddress: pairAddress ?? '',
      skip: 0,
    },
    {
      enabled: pairEnabled,
      queryKey: v2ChartQueryKey(chainId, { pairAddress: pairAddress ?? undefined, skip: 0, startTime }),
      staleTime: Infinity,
      refetchInterval: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: 3,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      select: (response: GetV2PairChartQuery) =>
        mapV2DayData(
          response.pairDayDatas.map(({ date, dailyVolumeUSD, tvlUSD }) => ({
            date,
            dailyVolumeUSD,
            tvlUSD,
          })),
          startTime
        ),
    }
  )

  const tokenQuery = useGetV2TokenChartQuery<AnalyticsChartSeries>(
    {
      endpoint,
    },
    {
      tokenAddress: tokenAddress ?? '',
      skip: 0,
    },
    {
      enabled: tokenEnabled,
      queryKey: v2ChartQueryKey(chainId, { tokenAddress: tokenAddress ?? undefined, skip: 0, startTime }),
      staleTime: Infinity,
      refetchInterval: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: 3,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      select: (response: GetV2TokenChartQuery) =>
        mapV2DayData(
          response.tokenDayDataV2S.map(({ date, dailyVolumeUSD, tvlUSD }) => ({
            date,
            dailyVolumeUSD,
            tvlUSD,
          })),
          startTime
        ),
    }
  )

  const globalQuery = useGetV2GlobalChartQuery<AnalyticsChartSeries>(
    {
      endpoint,
    },
    {
      skip: 0,
    },
    {
      enabled: globalEnabled,
      queryKey: v2ChartQueryKey(chainId, { skip: 0, startTime }),
      staleTime: Infinity,
      refetchInterval: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: 3,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      select: (response: GetV2GlobalChartQuery) =>
        mapV2DayData(
          response.uniswapDayDataV2S.map(({ date, dailyVolumeUSD, tvlUSD }) => ({
            date,
            dailyVolumeUSD,
            tvlUSD,
          })),
          startTime
        ),
    }
  )

  const activeQuery = pairEnabled ? pairQuery : tokenEnabled ? tokenQuery : globalQuery

  const prepared = activeQuery.data
  const rawData = prepared?.raw ?? []

  return {
    ...activeQuery,
    data: rawData,
    prepared,
    latestEntry: prepared?.latestEntry,
    tvlSeries: prepared?.tvl,
    volumeSeries: prepared?.volume,
    feesSeries: prepared?.fees,
    isLoading: activeQuery.isLoading,
    isFetching: activeQuery.isFetching,
    error: activeQuery.error ?? undefined,
  }
}
